import { initializeApp } from "firebase/app";
import { 
  getAuth, 
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPhoneNumber,
  signOut, 
  onAuthStateChanged,
  RecaptchaVerifier,
  PhoneAuthProvider,
  signInWithCredential,
  sendPasswordResetEmail,
  Auth
} from "firebase/auth";

import { getFirebaseConfig } from './firebase-config';

// Get Firebase configuration
const firebaseConfig = getFirebaseConfig();

// Validate Firebase configuration
if (!firebaseConfig.apiKey || !firebaseConfig.projectId || !firebaseConfig.appId) {
  console.error('Firebase configuration is missing required fields');
  console.error('Config:', firebaseConfig);
  throw new Error('Firebase configuration incomplete');
}

console.log('Initializing Firebase with config:', {
  apiKey: firebaseConfig.apiKey.substring(0, 10) + '...',
  authDomain: firebaseConfig.authDomain,
  projectId: firebaseConfig.projectId
});

let app;
let auth: Auth;

try {
  app = initializeApp(firebaseConfig);
  auth = getAuth(app);
  console.log('Firebase initialized successfully');
} catch (error) {
  console.error('Firebase initialization error:', error);
  // Try reinitializing with explicit app name
  try {
    app = initializeApp(firebaseConfig, 'blackbook-app');
    auth = getAuth(app);
    console.log('Firebase initialized with named app');
  } catch (secondError) {
    console.error('Firebase second initialization attempt failed:', secondError);
    throw secondError;
  }
}

export { auth };

// Email authentication functions
export const signInWithEmail = async (email: string, password: string) => {
  try {
    console.log('Attempting email sign-in...');
    const result = await signInWithEmailAndPassword(auth, email, password);
    console.log('Email sign-in successful:', result.user);
    return result;
  } catch (error) {
    console.error('Email sign-in error:', error);
    throw error;
  }
};

export const createAccountWithEmail = async (email: string, password: string) => {
  try {
    console.log('Creating account with email...');
    const result = await createUserWithEmailAndPassword(auth, email, password);
    console.log('Account creation successful:', result.user);
    return result;
  } catch (error) {
    console.error('Account creation error:', error);
    throw error;
  }
};

export const resetPassword = async (email: string) => {
  try {
    console.log('Sending password reset email...');
    await sendPasswordResetEmail(auth, email);
    console.log('Password reset email sent');
  } catch (error) {
    console.error('Password reset error:', error);
    throw error;
  }
};

export const setupRecaptcha = (containerId: string) => {
  return new RecaptchaVerifier(auth, containerId, {
    'size': 'normal',
    'callback': () => {
      // reCAPTCHA solved, allow signInWithPhoneNumber
    },
    'expired-callback': () => {
      // Response expired, ask user to solve reCAPTCHA again
    }
  });
};

export const signInWithPhone = (phoneNumber: string, recaptchaVerifier: RecaptchaVerifier) => {
  return signInWithPhoneNumber(auth, phoneNumber, recaptchaVerifier);
};

export const verifyPhoneCode = (verificationId: string, code: string) => {
  const credential = PhoneAuthProvider.credential(verificationId, code);
  return signInWithCredential(auth, credential);
};

export const logout = () => {
  return signOut(auth);
};

export const getCurrentUser = () => {
  return new Promise((resolve) => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      unsubscribe();
      resolve(user);
    });
  });
};